import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';

/** Tenant hint card from the session-scoped lounge GET (camelCase on wire). */
export type OralExamLoungeHint = {
  title: string;
  body: string;
};

/** Lounge payload for cohort waiting (orientation film + bed are optional). */
export type OralExamLoungePayload = {
  hints: OralExamLoungeHint[];
  orientationVideoUrl: string | null;
  waitingBedUrl: string | null;
};

export const EMPTY_ORAL_EXAM_LOUNGE: OralExamLoungePayload = {
  hints: [],
  orientationVideoUrl: null,
  waitingBedUrl: null,
};

/**
 * Tenant lounge hints win when the list is non-empty. Otherwise 1:N waiting
 * uses the product answering deck. 1:1 (`enabled: false`) shows no cards.
 */
export function resolveWaitingLoungeHints(
  tenantHints: OralExamLoungeHint[] | undefined,
  productDefaults: OralExamLoungeHint[],
  enabled: boolean,
): OralExamLoungeHint[] {
  if (!enabled) return [];
  if (tenantHints && tenantHints.length > 0) return tenantHints;
  return productDefaults;
}

function loungePath(ids: OralExamRouteIds): string {
  return `/courses/${ids.courseId}/modules/${ids.moduleId}/tasks/${ids.taskId}/bookings/${ids.bookingId}/lounge`;
}

function orientationCompletedPath(ids: OralExamRouteIds): string {
  return `/courses/${ids.courseId}/modules/${ids.moduleId}/tasks/${ids.taskId}/bookings/${ids.bookingId}/orientation-completed`;
}

export function getOralExamLoungeQueryKey(ids: OralExamRouteIds) {
  return [
    'oralExamLounge',
    ids.courseId,
    ids.moduleId,
    ids.taskId,
    ids.bookingId,
  ] as const;
}

/**
 * Session-scoped lounge GET. Transport/HTTP failures must throw so React Query
 * stays `isError` — a swallowed empty payload looks like “confirmed no film”
 * and would auto-stamp orientation. A 200 with a missing body is a real empty
 * lounge.
 */
export async function fetchOralExamLounge(ids: OralExamRouteIds): Promise<OralExamLoungePayload> {
  const response = await axios.get<{ data: OralExamLoungePayload }>(loungePath(ids));
  const payload = response.data?.data;
  if (!payload) return EMPTY_ORAL_EXAM_LOUNGE;
  return {
    hints: payload.hints ?? [],
    orientationVideoUrl: payload.orientationVideoUrl ?? null,
    waitingBedUrl: payload.waitingBedUrl ?? null,
  };
}

export function useOralExamLounge(ids: OralExamRouteIds, enabled: boolean) {
  return useQuery({
    queryKey: getOralExamLoungeQueryKey(ids),
    queryFn: () => fetchOralExamLounge(ids),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** Stamp orientation completion (If-Match guarded). Used when film ends or is skipped. */
export async function postOralExamOrientationCompleted(
  ids: OralExamRouteIds,
  etag: string | null | undefined,
) {
  const headers = etag ? { 'If-Match': etag } : undefined;
  return axios.post(orientationCompletedPath(ids), undefined, { headers });
}
